// ============================================================================
// LE CODE ARTICLE D'UN ARTICLE QUI NAÎT — proposé libre, et nommé quand il est pris.
//
// UTILITÉ : les deux assistants de création (« Article simple », « Gamme ») et la fenêtre « Dupliquer » proposent un code
// article. Le code est la clé du MODÈLE : sous un code déjà porté, la création s'ajouterait à l'article qui le porte et ses
// champs écraseraient ceux de l'original — et deux désignations longues qui commencent pareil donnent le même code, tronqué
// à 24 caractères. Ce module dit, AVANT le clic, quel code partira et qui porte celui qu'on a saisi.
//
// ⚠️ LE SERVEUR EST LA RÈGLE : il refuse un code pris au moment d'écrire, même si cet écran ne l'a pas vu (second onglet,
//    lecture en échec). Ce qui est dit ici n'est qu'une courtoisie — mais une courtoisie qui évite le seul cas grave.
// ⚠️ ON ATTEND LA VÉRIFICATION AVANT D'ÉCRIRE, code proposé compris : la duplication part du code de l'article d'origine,
//    toujours pris — l'envoyer avant la réponse serait un refus assuré. Une lecture en ÉCHEC, elle, ne bloque pas : elle se
//    DIT, et l'écriture tranchera avec son motif.
// ============================================================================
import { useEffect, useState } from 'react';
import { proposeArticleCode } from '@orgazone/core-catalog';
import { confirm } from '@orgazone/ui-web';
import { api, type ArticleCodeHolder } from '../api';
import { t } from '../i18n';

/** Ce que l'écran sait du code qui partira. */
export interface ArticleCodeCheck {
  /** Le code qui partira : celui qu'on a saisi, sinon le premier libre — vide quand aucun suffixe n'est libre. */
  effective: string;
  /** Le code proposé quand on n'a rien saisi — affiché dans le champ vide. */
  proposed: string;
  /** L'article qui porte le code saisi — ou, sans saisie, celui qui porte le code tiré de la désignation. */
  holder: ArticleCodeHolder | null;
  /** Le code SAISI est déjà porté. */
  taken: boolean;
  /** Le premier code libre à partir du code saisi quand il est pris — ce que « Prendre … » pose ; `null` s'il n'y en a aucun. */
  freeAlternative: string | null;
  /** La vérification n'est pas encore revenue. */
  checking: boolean;
  /** La création doit attendre : vérification en cours, ou code saisi déjà porté. */
  blocks: boolean;
  /** Le motif d'une vérification impossible — l'écran le dit, l'écriture tranchera. */
  failure: string | null;
}

/** Délai de frappe avant de demander au serveur : une lecture par mot, pas une par lettre. */
const CHECK_DELAY_MS = 250;

/**
 * VÉRIFIE le code d'un article qui naît, à chaque frappe (après un court délai).
 * `name` donne le code proposé quand `typed` est vide — sauf `proposedBase`, la base imposée (la duplication part du code
 * de l'article d'origine tant que la désignation n'a pas changé) ; `active` faux suspend tout (fenêtre fermée).
 */
export function useArticleCodeCheck(active: boolean, name: string, typed: string, proposedBase?: string): ArticleCodeCheck {
  const entered = typed.trim();
  const base = entered || proposedBase || (name.trim() ? proposeArticleCode(name) : '');
  const [reply, setReply] = useState<{ base: string; code: string | null; free: boolean; holder: ArticleCodeHolder | null } | null>(null);
  const [failure, setFailure] = useState<{ base: string; message: string } | null>(null);

  useEffect(() => {
    if (!active || !base) return;
    let alive = true;
    const timer = setTimeout(() => {
      api.freeArticleCode(base)
        .then((r) => { if (alive) { setReply({ base, ...r }); setFailure(null); } })
        .catch((e: unknown) => { if (alive) setFailure({ base, message: e instanceof Error ? e.message : String(e) }); });
    }, CHECK_DELAY_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [active, base]);

  // ⚠️ UNE RÉPONSE NE VAUT QUE POUR LA BASE QU'ELLE A LUE : celle du mot précédent se tairait sur le mot courant.
  const r = reply?.base === base ? reply : null;
  const f = failure?.base === base ? failure.message : null;
  const checking = !!base && !r && !f;
  if (entered) {
    const taken = r ? !r.free : false;
    return {
      effective: entered, proposed: entered, holder: r?.holder ?? null, taken,
      freeAlternative: taken ? r!.code : null,
      checking, blocks: checking || taken, failure: f,
    };
  }
  // Sans saisie : le premier libre ; la base tant qu'on ne sait pas, et l'écriture attend la réponse.
  const proposed = r ? (r.code ?? '') : base;
  return { effective: proposed, proposed, holder: r?.holder ?? null, taken: false, freeAlternative: null, checking, blocks: checking, failure: f };
}

/**
 * CE QUI S'AFFICHE SOUS LE CHAMP — le comportement, puis ce que le serveur a dit.
 * ⚠️ « AJOUTER UNE DÉCLINAISON À CET ARTICLE » OUVRE SA FICHE, jamais une création ici : sur la fiche, la case nouvelle
 *    hérite du modèle et rien d'autre ne change — c'est le seul chemin où rattacher n'écrase rien.
 */
export function ArticleCodeNotice({ check, typed, onTake, onOpenHolder }: {
  check: ArticleCodeCheck;
  typed: string;
  /** Pose le code libre proposé à la place du code saisi. */
  onTake: (code: string) => void;
  /** Ouvre la fiche de l'article qui porte le code — absent là où l'écran ne sait pas l'ouvrir. */
  onOpenHolder?: (holder: ArticleCodeHolder) => void;
}) {
  const help = { margin: '8px 0 0', fontSize: '11.5px', color: 'var(--ink-400)', lineHeight: 1.5 } as const;
  if (check.failure) {
    return <p role="status" style={{ ...help, color: 'var(--amber-fg)' }}>{t('Code non vérifié : {motif} — la création refusera un code déjà porté.', { motif: check.failure })}</p>;
  }
  const h = check.holder;
  if (typed.trim() && h && check.taken) {
    const free = check.freeAlternative;
    return (
      <div role="alert" style={{
        marginTop: 8, padding: '10px 12px', borderRadius: 'var(--r-sm)',
        background: 'var(--amber-bg)', border: '1px solid var(--amber)', fontSize: '12px', color: 'var(--ink-800)', lineHeight: 1.5,
      }}>
        <span style={{ display: 'block' }}>
          {t('Ce code est celui de « {nom} » ({declinaisons}).', { nom: h.name, declinaisons: t('{n} déclinaison', { n: h.variantCount }) })}
        </span>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {free && (
            <button type="button" className="btn-cta" style={{ height: 30, fontSize: '12px' }} onClick={() => onTake(free)}>
              {t('Prendre {code}', { code: free })}
            </button>
          )}
          {onOpenHolder && (
            <button type="button" className="btn-ghost" style={{ height: 30, fontSize: '12px' }} onClick={() => onOpenHolder(h)}>
              {t('Ajouter une déclinaison à cet article')}
            </button>
          )}
        </span>
      </div>
    );
  }
  if (!typed.trim() && h && !check.proposed) {
    return <p role="status" style={{ ...help, color: 'var(--amber-fg)' }}>{t('{base} et ses suffixes sont tous pris : saisissez un code.', { base: h.modelKey })}</p>;
  }
  if (!typed.trim() && h) {
    return <p style={help}>{t('{base} est le code de « {nom} » : {code} est proposé — modifiable.', { base: h.modelKey, nom: h.name, code: check.proposed })}</p>;
  }
  return <p style={help}>{t('Regroupe toutes les tailles et couleurs. Proposé, modifiable.')}</p>;
}

/**
 * QUITTER UNE CRÉATION POUR LA FICHE DE L'ARTICLE QUI PORTE LE CODE — après confirmation : la saisie en cours, codes-barres
 * bipés compris, n'est pas gardée, et un clic ne doit pas la jeter sans le dire.
 */
export async function leaveForHolder(
  holder: ArticleCodeHolder, close: () => void, openHolder: (h: ArticleCodeHolder) => void,
): Promise<void> {
  const accepted = await confirm({
    title: t('Abandonner cette création ?'),
    message: t('La fiche de « {nom} » s’ouvre sur ses déclinaisons ; ce que vous avez saisi ici n’est pas gardé.', { nom: holder.name }),
    confirmLabel: t('Ouvrir sa fiche'),
  });
  if (!accepted) return;
  close();
  openHolder(holder);
}
